import { useEffect, useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useCreateProgressRunMutation,
  useGetProgressObjectProfileQuery,
  useGetProgressScopeOptionsQuery,
  useListProgressRunsQuery,
} from '@shared/api';
import {
  apiErrorMessage,
  fmtDateTime,
  isProgressRunActive,
  runObjectLabel,
  statusLabel,
  statusTone,
  useProgressPermission,
  type ProgressRun,
} from '@entities/progress';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { popIn, riseIn } from '@shared/theme/animations';
import { IconArrowRight, IconClipboardCheck } from '@shared/ui/icons';

/* «Сводные отчёты» агента ОСНОВА.Руководитель. Слева — панель запуска сводки
   (пикер объекта/источников + крупный CTA), справа — лента последних отчётов.
   С дашборда объекта сюда приходят с `?project=`: объект выбран, смета и серия
   совещаний подставлены из привязок объекта. */

const Deck = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

/* ── Панель запуска ─────────────────────────────────────────────────────── */

const LaunchCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing(6)};
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const HeadIcon = styled.span`
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: ${({ theme }) => theme.radius.md};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const Lead = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(5)};
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FieldHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin: ${({ theme }) => `${theme.spacing(2)} 0 ${theme.spacing(3)}`};
`;

const Sources = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: ${({ theme }) => `${theme.spacing(5)} 0 ${theme.spacing(5)}`};
  padding-top: ${({ theme }) => theme.spacing(4)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const SourcesLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

const SourcePill = styled.span<{ $index: number }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  animation: ${popIn} 320ms ${({ theme }) => theme.motion.spring} both;
  animation-delay: ${({ $index }) => `${120 + $index * 70}ms`};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const SOURCES = ['ПТО', 'Сметы', 'Поле', 'Совещания'] as const;

/* ── Лента последних отчётов ────────────────────────────────────────────── */

const SideHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const SideTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const AllLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};
  }
  &:hover > svg {
    transform: translateX(3px);
  }

  &:focus-visible {
    outline: 2px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.focus};
    border-radius: ${({ theme }) => theme.radius.sm};
  }
`;

const RecentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const RecentRow = styled.button<{ $index: number }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  text-align: left;
  width: 100%;
  font: inherit;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  /* backwards-fill — чтобы hover-transform не перебивался финальным кадром. */
  animation: ${riseIn} 340ms ${({ theme }) => theme.motion.easeOut} backwards;
  animation-delay: ${({ $index }) => `${$index * 55}ms`};
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    transform ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    transform: translateX(2px);
    border-color: ${({ theme }) => theme.colors.borderStrong};
    box-shadow: ${({ theme }) => theme.shadow.sm};
  }

  &:focus-visible {
    outline: 2px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const RecentMain = styled.div`
  min-width: 0;
`;

const RecentName = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RecentDate = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const EmptyHint = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => `${theme.spacing(4)} 0`};
`;

/**
 * Роут `/agents/progress/reports` — сводные отчёты по ходу строительства. Слева
 * форма запуска (объект обязателен; контрактная смета, серия совещаний и имя
 * объекта — опционально уточняют источники), справа — последние отчёты. Запуск
 * создаёт прогон и переводит на его карточку.
 */
export function ProgressReports() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { can_read, can_write } = useProgressPermission();
  const { data: options, isLoading: optionsLoading } = useGetProgressScopeOptionsQuery(undefined, {
    skip: !can_read,
  });
  const [createRun, { isLoading: creating }] = useCreateProgressRunMutation();

  const runsQ = useListProgressRunsQuery(undefined, { skip: !can_read });
  const runs = useMemo<ProgressRun[]>(() => runsQ.data ?? [], [runsQ.data]);
  const recent = useMemo(() => runs.slice(0, 5), [runs]);
  // Пока есть активные отчёты — мягкий polling, чтобы лента «оживала».
  const hasActive = useMemo(() => runs.some((r) => isProgressRunActive(r.status)), [runs]);
  useListProgressRunsQuery(undefined, { skip: !can_read || !hasActive, pollingInterval: 5000 });

  const [projectId, setProjectId] = useState(() => params.get('project') ?? '');
  const [estimateId, setEstimateId] = useState('');
  const [seriesId, setSeriesId] = useState('');
  // Смета и серия совещаний из привязок объекта — подставляются при выборе
  // объекта; руководитель может их поменять для конкретного отчёта.
  const { data: objectProfile } = useGetProgressObjectProfileQuery(projectId, {
    skip: !can_read || !projectId,
  });
  useEffect(() => {
    if (!objectProfile || objectProfile.object.id !== projectId) return;
    setEstimateId(objectProfile.profile?.contract_estimate_id ?? '');
    setSeriesId(objectProfile.profile?.meetings_series_id ?? '');
  }, [objectProfile, projectId]);
  const [objectName, setObjectName] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Поле «Объект» помечаем невалидным только после попытки отправки — не «с порога».
  const [submitted, setSubmitted] = useState(false);

  const projects = options?.projects ?? [];
  const estimates = options?.estimates ?? [];
  const series = options?.series ?? [];

  // Мягкая проверка прав: роут уже под ProtectedShell, но дублируем заглушку,
  // как в остальных разделах, чтобы не мигало содержимым без прав.
  if (!can_read) {
    return (
      <FallbackBanner tone="danger" title="Доступ запрещён">
        У вас нет прав на раздел «Руководитель».
      </FallbackBanner>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitted(true);
    if (!projectId) {
      setError('Выберите объект — он обязателен для формирования отчёта.');
      return;
    }
    try {
      const run = await createRun({
        project_id: projectId,
        contract_estimate_id: estimateId || null,
        meetings_series_id: seriesId || null,
        register_object_name: objectName.trim() || null,
      }).unwrap();
      navigate(`/agents/progress/runs/${run.id}`);
    } catch (err) {
      setError(
        apiErrorMessage(err) ??
          'Не удалось запустить формирование отчёта. Попробуйте снова; если ошибка повторяется — обратитесь к администратору.',
      );
      // eslint-disable-next-line no-console
      console.error('createProgressRun failed', err);
    }
  }

  return (
    <Deck>
      <LaunchCard>
        <PanelHead>
          <HeadIcon aria-hidden>
            <IconClipboardCheck size={20} />
          </HeadIcon>
          <PanelTitle>Новая сводка по объекту</PanelTitle>
        </PanelHead>
        <Lead>
          Выберите объект — агент соберёт показатели плана и факта, освоение и открытые поручения и
          подготовит пояснительную записку. Привязки сметы и серии совещаний уточняют источники; без
          них агент возьмёт данные по объекту.
        </Lead>

        <form onSubmit={handleSubmit}>
          <Field>
            <Label htmlFor="progress-project">Объект (проект)</Label>
            <Select
              id="progress-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={!can_write || optionsLoading}
              aria-invalid={submitted && !projectId}
              aria-describedby={error ? 'progress-project-error' : undefined}
              required
            >
              <option value="">
                {optionsLoading ? 'Загрузка объектов…' : '— выберите объект —'}
              </option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
            <FieldHint>Объект, по которому формируется сводный отчёт. Обязателен.</FieldHint>
          </Field>

          <Field>
            <Label htmlFor="progress-estimate">Контрактная смета</Label>
            <Select
              id="progress-estimate"
              value={estimateId}
              onChange={(e) => setEstimateId(e.target.value)}
              disabled={!can_write || estimates.length === 0}
            >
              <option value="">— не привязывать —</option>
              {estimates.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.title}
                </option>
              ))}
            </Select>
            <FieldHint>Источник плановых показателей. Необязательно.</FieldHint>
          </Field>

          <Field>
            <Label htmlFor="progress-series">Серия совещаний</Label>
            <Select
              id="progress-series"
              value={seriesId}
              onChange={(e) => setSeriesId(e.target.value)}
              disabled={!can_write || series.length === 0}
            >
              <option value="">— не привязывать —</option>
              {series.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.title}
                </option>
              ))}
            </Select>
            <FieldHint>Источник статусов и поручений по объекту. Необязательно.</FieldHint>
          </Field>

          <Field>
            <Label htmlFor="progress-object-name">Имя объекта в реестре</Label>
            <Input
              id="progress-object-name"
              type="text"
              value={objectName}
              onChange={(e) => setObjectName(e.target.value)}
              placeholder="Напр.: Котельная №3, г. Норильск"
              disabled={!can_write}
            />
            <FieldHint>
              Уточняет наименование объекта в отчёте, если у проекта нет привязки. Необязательно.
            </FieldHint>
          </Field>

          <Sources aria-label="Источники данных отчёта">
            <SourcesLabel>Источники</SourcesLabel>
            {SOURCES.map((s, i) => (
              <SourcePill key={s} $index={i}>
                {s}
              </SourcePill>
            ))}
          </Sources>

          {error && (
            <Err id="progress-project-error" role="alert">
              {error}
            </Err>
          )}
          <Button
            type="submit"
            $variant="primary"
            $size="lg"
            $block
            disabled={creating || !can_write || !projectId}
            title={!can_write ? 'Только просмотр' : undefined}
          >
            {creating ? 'Формирую…' : 'Сформировать сводку'}
          </Button>
        </form>
      </LaunchCard>

      <div>
        <SideHead>
          <SideTitle>Последние отчёты</SideTitle>
          <AllLink to="/agents/progress/runs">
            Все отчёты
            <IconArrowRight size={14} />
          </AllLink>
        </SideHead>

        {recent.length > 0 ? (
          <RecentList>
            {recent.map((r, i) => (
              <RecentRow
                key={r.id}
                $index={i}
                type="button"
                onClick={() => navigate(`/agents/progress/runs/${r.id}`)}
              >
                <RecentMain>
                  <RecentName title={runObjectLabel(r)}>{runObjectLabel(r)}</RecentName>
                  <RecentDate>{fmtDateTime(r.created_at)}</RecentDate>
                </RecentMain>
                <Badge $tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
              </RecentRow>
            ))}
          </RecentList>
        ) : (
          <EmptyHint>
            {runsQ.isLoading
              ? 'Загрузка…'
              : 'Пока нет отчётов. Выберите объект слева и сформируйте первую сводку.'}
          </EmptyHint>
        )}
      </div>
    </Deck>
  );
}
